/**
 * デモモード用 axios アダプタ
 *
 * DEMO_MODE=true のとき axios.defaults.adapter（およびappの共有 axiosInstance）に
 * 差し込まれ、バックエンドへ実際のリクエストを送らず demoData のフィクスチャで応答する。
 * 一部サービス（favoriteApi 等）は共有インスタンスではなく生 axios を直接使うため、
 * グローバル既定アダプタにも設置している（installDemoAdapter.ts 参照）。
 *
 * 方針:
 * - URL は正規化し、絶対URL/`/api` プレフィックスの有無を吸収する。
 * - 既知ルートはテーブルで明示的にハンドリングし、消費側が期待する形で返す。
 * - 未知ルートはUIを壊さない安全なデフォルトを返す。
 * - /auth/login のみ許可リストで認証し、失敗時は 401 を reject（既存のエラー表示と互換）。
 */

import { AxiosError } from 'axios';
import type { AxiosAdapter, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import {
  DEMO_ACCOUNTS,
  DEMO_TOKEN,
  DEMO_VIDEOS,
  DEMO_COMMENTS,
  DEMO_SPECIALTIES,
  DEMO_LIVE_STREAMS,
  DEMO_USERS,
  DEMO_LIKERS,
  DEMO_HOSPITAL,
} from './demoData';
import { SessionManager } from '../utils/SessionManager';
import { resolveSpecialtyAttributes, parseAttributeToken } from '../utils/specialtyAttributes';
import type { LoginRes, UserProfile } from '../services/authApi/authApi.types';
import type { Video, SpecialtyCountsRes } from '../services/videoApi/videoApi.types';
import { UNCLASSIFIED_SPECIALTY_KEY } from '../services/videoApi/videoApi.types';
import type { Comment } from '../services/commentApi/commentApi.types';
import type { User } from '../services/userApi/userApi.types';

type Json = Record<string, unknown> | unknown[] | string | number | boolean | null;

// セッション中だけ保持するコメントの作業ストア（投稿/編集/削除を一貫して反映する）
const runtimeComments: Comment[] = DEMO_COMMENTS.map(c => ({ ...c }));

// プロフィール編集をセッション中だけ反映する（保存が偽成功でプロフィールを消すのを防ぐ）
const runtimeProfiles: Record<string, UserProfile> = Object.fromEntries(
  DEMO_ACCOUNTS.map(a => [String(a.profile.id), { ...a.profile }])
);

// ユーザー管理のデモ名簿（作成をセッション中だけ反映する）
const runtimeUsers: User[] = DEMO_USERS.map(u => ({ ...u }));

// 現在ログイン中のユーザー（コメント投稿者として使う）
const currentUser = (): { id: string; name: string; icon_path: string | null } => {
  const s = SessionManager.getActiveSession();
  if (s) {
    return { id: String(s.userId), name: s.name || 'デモユーザー', icon_path: s.icon_path ?? null };
  }
  const fallback = DEMO_ACCOUNTS[0]?.user;
  return { id: String(fallback?.id ?? '1001'), name: fallback?.name ?? 'デモユーザー', icon_path: null };
};

// セッション内で一意なIDを払い出す
let idSeq = 100000;
const nextId = (): number => ++idSeq;

/**
 * セッション中ストアを初期状態へ戻す（主にテスト用）。
 * これらはモジュールレベルの状態なので localStorage.clear() では初期化されず、
 * テスト間で投稿・作成・改名がリークする。テストの beforeEach で呼ぶこと。
 */
export const resetDemoState = (): void => {
  runtimeComments.splice(0, runtimeComments.length, ...DEMO_COMMENTS.map(c => ({ ...c })));
  Object.keys(runtimeProfiles).forEach(k => delete runtimeProfiles[k]);
  DEMO_ACCOUNTS.forEach(a => {
    runtimeProfiles[String(a.profile.id)] = { ...a.profile };
  });
  runtimeUsers.splice(0, runtimeUsers.length, ...DEMO_USERS.map(u => ({ ...u })));
  idSeq = 100000;
};

// --- レスポンス生成ヘルパー ---
const ok = (config: InternalAxiosRequestConfig, data: Json, status = 200): AxiosResponse => ({
  data,
  status,
  statusText: status === 200 ? 'OK' : String(status),
  headers: {},
  config,
  request: {},
});

const fail = (
  config: InternalAxiosRequestConfig,
  status: number,
  message: string
): AxiosError => {
  // デモの「この操作はできません」(401/403) はセッション失効ではない。
  // services/api.ts の axiosInstance は 401 と（接続元ゲート以外の）403 を
  // セッション失効とみなし removeSession → /login へ飛ばす。
  // utils/axiosInterceptor.js は #334 以降 401 のみが対象。
  // どちらの経路でも“強制ログアウト”を避けるため X-Skip-Auth-Error を付ける。
  if (status === 401 || status === 403) {
    if (!config.headers) config.headers = {} as InternalAxiosRequestConfig['headers'];
    (config.headers as unknown as Record<string, string>)['X-Skip-Auth-Error'] = 'true';
  }
  const response: AxiosResponse = {
    data: { error: message, message, status },
    status,
    statusText: String(status),
    headers: {},
    config,
    request: {},
  };
  return new AxiosError(message, String(status), config, {}, response);
};

// --- URL を /api 相対パスに正規化（絶対URL・/apiプレフィックスを吸収）---
const toPath = (config: InternalAxiosRequestConfig): string => {
  let u = (config.url || '').split('?')[0];
  u = u.replace(/^https?:\/\/[^/]+/, ''); // プロトコル+ホストを除去
  u = u.replace(/^\/api(?=\/)/, ''); // 先頭の /api を除去
  if (!u.startsWith('/')) u = '/' + u;
  return u;
};

// --- リクエストの中身を取り出す ---
const parseBody = (config: InternalAxiosRequestConfig): Record<string, unknown> => {
  const { data } = config;
  if (!data) return {};
  if (typeof data === 'string') {
    try {
      return JSON.parse(data) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  if (typeof data === 'object') return data as Record<string, unknown>;
  return {};
};

const getParam = (config: InternalAxiosRequestConfig, key: string): string | undefined => {
  const params = config.params as Record<string, unknown> | undefined;
  const v = params?.[key];
  return v === undefined || v === null ? undefined : String(v);
};

const toNum = (v: string | undefined, fallback: number): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

// --- 一覧の診療科フィルタ（本番の /videos/paginated?specialty= と同じ意味） ---
// 'unclassified' は診療科未設定の動画、それ以外は診療科IDで絞る。未指定なら全件
export const filterDemoVideosBySpecialty = (videos: Video[], specialty: string | undefined): Video[] => {
  if (!specialty) return videos;
  if (specialty === UNCLASSIFIED_SPECIALTY_KEY) return videos.filter(v => !v.specialty);
  return videos.filter(v => v.specialty && String(v.specialty.id) === specialty);
};

// いま見える動画が1本以上ある診療科と、診療科未設定の本数（空の科は返さない）。
// 本番の GET /videos/specialty-counts（videoController.getSpecialtyCounts）と同じ意味
export const countDemoVideosBySpecialty = (videos: Video[]): SpecialtyCountsRes => {
  const counts = new Map<string, { id: string; name: string; count: number }>();
  let unclassified = 0;
  videos.forEach(v => {
    if (!v.specialty) {
      unclassified += 1;
      return;
    }
    const id = String(v.specialty.id);
    const entry = counts.get(id) ?? { id, name: v.specialty.name, count: 0 };
    entry.count += 1;
    counts.set(id, entry);
  });
  return {
    specialties: Array.from(counts.values()).sort((a, b) => a.name.localeCompare(b.name, 'ja')),
    unclassified_count: unclassified,
  };
};

// --- 動画一覧のページネーション ---
const paginate = (videos: Video[], config: InternalAxiosRequestConfig) => {
  const page = toNum(getParam(config, 'page'), 1);
  const limit = toNum(getParam(config, 'limit'), 10);
  const start = (page - 1) * limit;
  const slice = videos.slice(start, start + limit);
  return {
    videos: slice,
    pagination: {
      totalItems: videos.length,
      currentPage: page,
      totalPages: Math.max(1, Math.ceil(videos.length / limit)),
      pageSize: limit,
    },
  };
};

/**
 * デモの動画検索。
 *
 * 素朴な部分一致に加えて `診療科:整形外科` のような接頭辞付きクエリを解釈する。
 * 動画詳細の分類チップ（診療科・部位・AO分類）と分類ボックスはこの形で
 * `/?q=...` に飛ばしてくるため、接頭辞を解釈しないと**押すたびに必ず0件**になる。
 * 実映像化で全5本に部位とAO分類が付いたことで、この死んだリンクが15個に増えた。
 * 「分類から探せる」はプロダクトの売りなので、デモでは動く必要がある。
 */
export const searchDemoVideos = (rawQuery: string): Video[] => {
  const q = (rawQuery || '').trim();
  if (!q) return DEMO_VIDEOS;

  const PREFIXES: { prefix: string; pick: (v: Video) => string | undefined }[] = [
    { prefix: '診療科:', pick: v => v.specialty?.name },
    { prefix: '病院:', pick: v => v.uploader?.hospital?.name },
  ];

  const matched = PREFIXES.find(p => q.startsWith(p.prefix));
  if (matched) {
    const value = q.slice(matched.prefix.length).trim();
    return DEMO_VIDEOS.filter(v => matched.pick(v) === value);
  }

  // `部位:膝` のようなキー付きタグ。本番の検索と同じく、どのキーでも属性を同じ形で見る
  const attributeToken = parseAttributeToken(q);
  if (attributeToken) {
    return DEMO_VIDEOS.filter(v =>
      resolveSpecialtyAttributes(v).some(
        a => a.attribute_key === attributeToken.attribute_key && a.attribute_value === attributeToken.attribute_value
      )
    );
  }

  return DEMO_VIDEOS.filter(
    v => (v.title || '').includes(q) || (v.tags || '').includes(q)
  );
};

const emptyList = (config: InternalAxiosRequestConfig) => ({
  videos: [],
  pagination: {
    totalItems: 0,
    currentPage: toNum(getParam(config, 'page'), 1),
    totalPages: 0,
    pageSize: toNum(getParam(config, 'limit'), 10),
  },
});

/**
 * デモアダプタ本体。正規化した path + method でルーティングする。
 */
export const demoAdapter: AxiosAdapter = (config) => {
  const method = (config.method || 'get').toLowerCase();
  const url = toPath(config);

  return new Promise<AxiosResponse>((resolve, reject) => {
    // ---- 認証 ----
    if (url === '/auth/login' && method === 'post') {
      const body = parseBody(config);
      const email = String(body.email ?? '').trim();
      const password = String(body.password ?? '');
      const account = DEMO_ACCOUNTS.find(a => a.email === email && a.password === password);
      if (!account) {
        reject(fail(config, 401, 'メールアドレスまたはパスワードが正しくありません。'));
        return;
      }
      // 社外配布した一時アカウントの失効。資格情報が合っていても期限切れなら通さない。
      // 「合っていない」と同じ文言にすると相手が原因を切り分けられないため区別する。
      if (account.expiresAt && Date.now() > new Date(account.expiresAt).getTime()) {
        reject(
          fail(config, 401, 'このアカウントの有効期限が切れています。担当者にお問い合わせください。')
        );
        return;
      }
      const res: LoginRes = {
        token: DEMO_TOKEN,
        user: account.user,
        requires_terms_agreement: false,
        current_terms: null,
      };
      resolve(ok(config, res as unknown as Json));
      return;
    }
    if (url === '/auth/me' && method === 'get') {
      // ログイン中のアカウントを返す（誰でログインしても demo太郎 になる不整合を解消）
      const session = SessionManager.getActiveSession();
      const id = session ? String(session.userId) : String(DEMO_ACCOUNTS[0]?.profile.id ?? '');
      const profile = runtimeProfiles[id] ?? DEMO_ACCOUNTS[0]?.profile ?? null;
      resolve(ok(config, profile as unknown as Json));
      return;
    }
    if (url === '/auth/logout' && method === 'post') {
      resolve(ok(config, { success: true, message: 'logged out' }));
      return;
    }
    // ---- プロフィール編集（偽成功で表示を壊さないよう、更新後の値を返す）----
    if (url === '/auth/profile' && method === 'put') {
      const body = parseBody(config);
      const session = SessionManager.getActiveSession();
      const id = session ? String(session.userId) : String(DEMO_ACCOUNTS[0]?.profile.id ?? '');
      const base = runtimeProfiles[id] ?? DEMO_ACCOUNTS[0].profile;
      const updated: UserProfile = {
        ...base,
        ...(body.name !== undefined ? { name: String(body.name) } : {}),
        updated_at: new Date().toISOString(),
      };
      runtimeProfiles[id] = updated;
      // ユーザー管理の名簿にも反映する（アカウント画面で改名したのに
      // /admin/users では旧名のまま、という食い違いを防ぐ）
      const listed = runtimeUsers.find(u => String(u.id) === id);
      if (listed) {
        listed.name = updated.name;
        listed.updated_at = updated.updated_at;
      }
      // updateProfile() は res.data.user を取り出す実装（実バックエンドの
      // PUT /api/auth/profile が { message, user } を返す仕様に合わせている）ため、
      // ここも同じ形状で返す。UserProfile を直接返すと response.data.user が
      // undefined になり、デモモードでの改名が偽成功（表示に反映されない）になる。
      resolve(ok(config, { message: 'Profile updated', user: updated } as unknown as Json));
      return;
    }
    if (url === '/auth/profile/icon' && method === 'put') {
      const session = SessionManager.getActiveSession();
      const id = session ? String(session.userId) : String(DEMO_ACCOUNTS[0]?.profile.id ?? '');
      const profile = runtimeProfiles[id] ?? DEMO_ACCOUNTS[0].profile;
      resolve(ok(config, profile as unknown as Json));
      return;
    }
    if (url === '/auth/change-password' && method === 'put') {
      resolve(ok(config, { success: true, message: 'パスワードを変更しました' } as unknown as Json));
      return;
    }

    // ---- 利用規約（デモでは同意済み扱い）----
    if (url === '/terms/current' && method === 'get') {
      resolve(ok(config, { terms: null } as unknown as Json));
      return;
    }
    if (url === '/terms/user-agreement-status' && method === 'get') {
      // ガードは has_agreed_to_current を見るため、同意済みとして返す
      resolve(ok(config, { has_agreed_to_current: true } as unknown as Json));
      return;
    }
    if (url === '/terms/agree' && method === 'post') {
      resolve(ok(config, { success: true, message: '同意しました' } as unknown as Json));
      return;
    }

    // ---- 動画 ----
    if (url === '/videos' && method === 'get') {
      resolve(ok(config, DEMO_VIDEOS as unknown as Json));
      return;
    }
    if (url === '/videos/paginated' && method === 'get') {
      const specialty = getParam(config, 'specialty');
      resolve(ok(config, paginate(filterDemoVideosBySpecialty(DEMO_VIDEOS, specialty), config) as unknown as Json));
      return;
    }
    if (url === '/videos/search' && method === 'get') {
      const q = (getParam(config, 'q') || '').trim();
      const specialty = getParam(config, 'specialty');
      resolve(ok(config, paginate(filterDemoVideosBySpecialty(searchDemoVideos(q), specialty), config) as unknown as Json));
      return;
    }
    if (url === '/videos/specialty-counts' && method === 'get') {
      resolve(ok(config, countDemoVideosBySpecialty(DEMO_VIDEOS) as unknown as Json));
      return;
    }
    if (url === '/videos/suggestions' && method === 'get') {
      const q = (getParam(config, 'q') || '').trim();
      const suggestions = DEMO_VIDEOS
        .filter(v => !q || (v.title || '').includes(q))
        .map(v => ({ id: v.id, type: 'video', value: v.title || '', display_text: v.title }));
      resolve(ok(config, suggestions as unknown as Json));
      return;
    }
    if (url === '/videos/related' && method === 'get') {
      const currentId = getParam(config, 'current_video_id');
      const related = DEMO_VIDEOS.filter(v => String(v.id) !== String(currentId));
      resolve(ok(config, { videos: related } as unknown as Json));
      return;
    }
    if (url === '/videos/my' && method === 'get') {
      // 全動画の投稿者はログイン中のデモ太郎。ここを空にすると「自分で上げた
      // はずの動画がマイビデオに無い」ことになるので、投稿者一致で返す。
      // status（公開中/院内限定/非公開）での絞り込みにも追従させる。
      // fetchMyVideos は既定で status='all' を必ず送ってくる。これを
      // visibility と素朴に突き合わせると 'all' がどれにも一致せず空になる。
      const status = getParam(config, 'status');
      const mine = DEMO_VIDEOS.filter(
        v => String(v.uploader_id) === String(DEMO_ACCOUNTS[0].user.id)
      );
      const filtered =
        status && status !== 'all' ? mine.filter(v => v.visibility === status) : mine;
      resolve(ok(config, paginate(filtered, config) as unknown as Json));
      return;
    }
    if (
      (url === '/videos/favorite' || url === '/videos/liked') &&
      method === 'get'
    ) {
      resolve(ok(config, emptyList(config) as unknown as Json));
      return;
    }
    const videoIdMatch = url.match(/^\/videos\/(\w+)$/);
    if (videoIdMatch && method === 'get') {
      const id = videoIdMatch[1];
      const video = DEMO_VIDEOS.find(v => String(v.id) === id);
      if (!video) {
        reject(fail(config, 404, 'Video not found'));
        return;
      }
      resolve(ok(config, video as unknown as Json));
      return;
    }
    // ダウンロードは明示的に拒否する。
    //
    // UI 側は KebabMenu が isOwner でボタンを出し分けており、投稿者(id 1001)以外の
    // アカウントには出ない。ただしフォールバックに落ちると GET は [] を返して
    // 「成功したが空」になり、videoDownload.ts が壊れた挙動をする。明示的に 403 を返す。
    //
    // 注意: これは導線を塞ぐだけで、ダウンロードの防止にはなっていない。動画は
    // 公開S3の絶対URLで配信しているため、再生できる人はブラウザから実ファイルを
    // 取得できる。本気で止めるなら署名付きURL等が要る（demoData.ts の注記参照）。
    if (/^\/videos\/[^/]+\/download$/.test(url)) {
      reject(fail(config, 403, 'デモでは動画をダウンロードできません。'));
      return;
    }
    // アップロード系は明示的に無効化（UIでも非表示だが二重で塞ぐ）
    if (url === '/videos' && method === 'post') {
      reject(fail(config, 403, 'デモではアップロードできません。'));
      return;
    }
    // 動画メタデータの編集保存（デモでは永続化しないが、正しい形で返し表示を壊さない）
    const videoPutMatch = url.match(/^\/videos\/(\w+)$/);
    if (videoPutMatch && method === 'put') {
      const video = DEMO_VIDEOS.find(v => String(v.id) === videoPutMatch[1]);
      const body = parseBody(config);
      resolve(
        ok(config, {
          success: true,
          message: 'デモでは編集内容は保存されません（表示のみ）。',
          video: video ? { ...video, ...body } : undefined,
        } as unknown as Json)
      );
      return;
    }

    // ---- 外部動画 ----
    if (url === '/external-videos' && method === 'get') {
      resolve(ok(config, { videos: [] } as unknown as Json));
      return;
    }
    if (/^\/external-videos\/[^/]+\/related$/.test(url) && method === 'get') {
      resolve(ok(config, [] as unknown as Json));
      return;
    }

    // ---- ライブ配信（#221。デモは live/scheduled/ended を用意）----
    if (url === '/live-streams' && method === 'get') {
      const status = getParam(config, 'status');
      const list = status
        ? DEMO_LIVE_STREAMS.filter(s => s.status === status)
        : DEMO_LIVE_STREAMS;
      resolve(ok(config, { liveStreams: list } as unknown as Json));
      return;
    }
    if (/^\/live-streams\/[^/]+\/hls-session$/.test(url) && method === 'post') {
      // live の短命視聴URL。デモでは録画済みの動画をライブ映像として返す。
      // ファイル名を直書きすると S3 側の改名に追従できず、テストも
      // toBeTruthy() では気づけないまま「ライブを開くと404」になる。
      // 配信中(ls-1)のタイトルと揃えるため、動画1をそのまま流す。
      const liveSource = DEMO_VIDEOS.find(v => String(v.id) === '1') ?? DEMO_VIDEOS[0];
      resolve(ok(config, { hls_url: liveSource.path, expires_in: 3600 } as unknown as Json));
      return;
    }
    const liveEndMatch = url.match(/^\/live-streams\/([^/]+)\/end$/);
    if (liveEndMatch && method === 'post') {
      const s = DEMO_LIVE_STREAMS.find(x => x.id === liveEndMatch[1]);
      resolve(ok(config, { liveStream: s ? { ...s, status: 'ended' } : null } as unknown as Json));
      return;
    }
    if (url === '/live-streams' && method === 'post') {
      reject(fail(config, 403, 'デモではライブ配信を開始できません。'));
      return;
    }
    const liveIdMatch = url.match(/^\/live-streams\/([^/]+)$/);
    if (liveIdMatch && method === 'get') {
      const s = DEMO_LIVE_STREAMS.find(x => x.id === liveIdMatch[1]);
      if (!s) {
        reject(fail(config, 404, 'ライブ配信が見つかりません'));
        return;
      }
      resolve(ok(config, { liveStream: s } as unknown as Json));
      return;
    }

    // ---- お気に入り ----
    if (/^\/favorites\/check\/[^/]+$/.test(url) && method === 'get') {
      resolve(ok(config, { is_favorite: false } as unknown as Json));
      return;
    }
    if (url === '/favorites' && method === 'get') {
      resolve(ok(config, { favorites: [] } as unknown as Json));
      return;
    }
    if (url.startsWith('/favorites')) {
      // 登録/解除はデモでは no-op success
      resolve(ok(config, { success: true } as unknown as Json));
      return;
    }

    // ---- いいね ----
    if (url === '/video-likes/count' && method === 'get') {
      resolve(ok(config, { count: 0, likesCount: 0 } as unknown as Json));
      return;
    }
    // いいねした人モーダル: likers を返さないと likers.map で落ちる
    if (url === '/video-likes/likers' && method === 'get') {
      resolve(ok(config, { likers: DEMO_LIKERS } as unknown as Json));
      return;
    }
    if (url === '/video-likes/check' && method === 'get') {
      resolve(ok(config, { is_liked: false } as unknown as Json));
      return;
    }
    if (url.startsWith('/video-likes')) {
      resolve(ok(config, { success: true, liked: false } as unknown as Json));
      return;
    }

    // ---- 視聴記録（GET: レコード無し=null / POST: 記録レスポンス）----
    if (/^\/watch-records\/video\/[^/]+$/.test(url) && method === 'get') {
      resolve(ok(config, null));
      return;
    }
    if (url.startsWith('/watch-records')) {
      const body = parseBody(config);
      const record = {
        id: nextId(),
        user_id: currentUser().id,
        video_id: String(body.video_id ?? ''),
        watch_seconds: Number(body.watch_seconds ?? 0),
        progress_percent: Number(body.progress_percent ?? 0),
        view_count: 1,
        last_watched_at: new Date().toISOString(),
        max_position: Number(body.max_position ?? 0),
      };
      resolve(ok(config, { message: 'recorded', record } as unknown as Json));
      return;
    }

    // ---- コメント（インメモリ・ストアで投稿/編集/削除を反映）----
    if (url === '/comments' && method === 'get') {
      const videoId = getParam(config, 'video_id');
      const comments = runtimeComments.filter(
        c => String(c.video_id) === String(videoId) && !c.deleted_at
      );
      resolve(
        ok(config, {
          comments,
          pagination: { totalItems: comments.length, currentPage: 1, totalPages: 1, pageSize: 20 },
        } as unknown as Json)
      );
      return;
    }
    if (url === '/comments' && method === 'post') {
      const body = parseBody(config);
      const u = currentUser();
      const comment: Comment = {
        id: nextId(),
        video_id: String(body.video_id ?? ''),
        user_id: u.id,
        content: String(body.content ?? ''),
        created_at: new Date().toISOString(),
        user: { id: u.id, name: u.name, icon_path: u.icon_path },
        childCount: 0,
        likeCount: 0,
      };
      runtimeComments.unshift(comment);
      resolve(ok(config, { message: 'コメントを投稿しました', comment } as unknown as Json));
      return;
    }
    const commentIdMatch = url.match(/^\/comments\/(\w+)$/);
    if (commentIdMatch && method === 'put') {
      const id = Number(commentIdMatch[1]);
      const body = parseBody(config);
      const target = runtimeComments.find(c => Number(c.id) === id);
      if (target) {
        target.content = String(body.content ?? target.content);
        target.updated_at = new Date().toISOString();
        resolve(ok(config, target as unknown as Json));
      } else {
        // 対象が無い場合は偽の成功を返さず 404 にする
        reject(fail(config, 404, 'コメントが見つかりません。'));
      }
      return;
    }
    if (commentIdMatch && method === 'delete') {
      const id = Number(commentIdMatch[1]);
      const target = runtimeComments.find(c => Number(c.id) === id);
      if (target) target.deleted_at = new Date().toISOString();
      resolve(ok(config, { message: 'コメントを削除しました', id } as unknown as Json));
      return;
    }

    // ---- 子コメント（返信）----
    if (url === '/child-comments' && method === 'get') {
      resolve(ok(config, { childComments: [] } as unknown as Json));
      return;
    }
    if (url === '/child-comments' && method === 'post') {
      const body = parseBody(config);
      const u = currentUser();
      const childComment = {
        id: nextId(),
        comment_id: Number(body.comment_id ?? 0),
        user_id: Number(u.id) || 0,
        content: String(body.content ?? ''),
        created_at: new Date().toISOString(),
        user: { id: u.id, name: u.name, icon_path: u.icon_path },
      };
      resolve(ok(config, { childComment, message: '返信を投稿しました' } as unknown as Json));
      return;
    }
    if (/^\/child-comments\/\w+$/.test(url) && method === 'put') {
      const body = parseBody(config);
      resolve(
        ok(config, {
          childComment: { content: String(body.content ?? ''), updated_at: new Date().toISOString() },
          message: '返信を更新しました',
        } as unknown as Json)
      );
      return;
    }
    if (/^\/child-comments\/\w+$/.test(url) && method === 'delete') {
      resolve(ok(config, { success: true, message: '返信を削除しました' } as unknown as Json));
      return;
    }

    // ---- コメント/子コメントのいいね ----
    // count/status/likers は完全一致しないと空データで数値が消える/likers.map で落ちる
    if (url === '/comment-likes/count' && method === 'get') {
      const cid = getParam(config, 'comment_id');
      const c = runtimeComments.find(x => String(x.id) === String(cid));
      resolve(ok(config, { comment_id: cid ?? null, likesCount: c?.likeCount ?? 0 } as unknown as Json));
      return;
    }
    if (url === '/comment-likes/status' && method === 'get') {
      resolve(ok(config, { liked: false } as unknown as Json));
      return;
    }
    if (url === '/comment-likes/likers' && method === 'get') {
      const cid = getParam(config, 'comment_id');
      resolve(ok(config, { comment_id: cid ?? null, likers: [] } as unknown as Json));
      return;
    }
    if (url === '/child-comment-likes/count' && method === 'get') {
      resolve(
        ok(config, {
          child_comment_id: getParam(config, 'child_comment_id') ?? null,
          likesCount: 0,
        } as unknown as Json)
      );
      return;
    }
    if (url === '/child-comment-likes/status' && method === 'get') {
      resolve(ok(config, { liked: false } as unknown as Json));
      return;
    }
    if (url === '/child-comment-likes/likers' && method === 'get') {
      resolve(ok(config, { likers: [] } as unknown as Json));
      return;
    }
    if (url === '/comment-likes') {
      if (method === 'post') {
        const body = parseBody(config);
        resolve(
          ok(config, {
            message: 'いいねしました',
            commentLike: {
              comment_id: body.comment_id ?? null,
              user_id: currentUser().id,
              created_at: new Date().toISOString(),
            },
          } as unknown as Json)
        );
      } else {
        resolve(ok(config, { message: 'いいねを取り消しました' } as unknown as Json));
      }
      return;
    }
    if (url === '/child-comment-likes') {
      if (method === 'post') {
        const body = parseBody(config);
        resolve(
          ok(config, {
            message: 'いいねしました',
            childCommentLike: {
              child_comment_id: body.child_comment_id ?? null,
              user_id: currentUser().id,
              created_at: new Date().toISOString(),
            },
          } as unknown as Json)
        );
      } else {
        resolve(ok(config, { message: 'いいねを取り消しました' } as unknown as Json));
      }
      return;
    }

    // ---- 診療科 ----
    if (url === '/specialties' && method === 'get') {
      resolve(ok(config, { specialties: DEMO_SPECIALTIES } as unknown as Json));
      return;
    }

    // ---- 通知 ----
    // 管理者の全体通知配信などの破壊的操作は、成功に見せず明示的に拒否する
    if (url.startsWith('/notifications/admin') && method !== 'get') {
      reject(fail(config, 403, 'デモではこの操作はできません。'));
      return;
    }
    if (url.startsWith('/notifications')) {
      if (method === 'get') {
        resolve(ok(config, { notifications: [], unreadCount: 0 } as unknown as Json));
      } else {
        resolve(ok(config, { success: true } as unknown as Json));
      }
      return;
    }

    // ---- ユーザー管理（一覧はデモ名簿を返す。作成はセッション中だけ反映）----
    if (url === '/users' && method === 'get') {
      resolve(ok(config, runtimeUsers.filter(u => !u.deleted_at) as unknown as Json));
      return;
    }
    if (url === '/users' && method === 'post') {
      const body = parseBody(config);
      const user: User = {
        id: String(nextId()),
        email: String(body.email ?? ''),
        name: String(body.name ?? ''),
        role: 'user',
        hospital_id: DEMO_HOSPITAL.id,
        hospital_name: DEMO_HOSPITAL.name,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      runtimeUsers.push(user);
      // CreateUserRes は temporaryPassword が必須。欠けると完了画面のパスワード欄が空になる。
      resolve(
        ok(config, {
          message: 'ユーザーを作成しました',
          user,
          temporaryPassword: 'Demo-Temp-1234',
        } as unknown as Json)
      );
      return;
    }
    const userGetMatch = url.match(/^\/users\/([^/]+)$/);
    if (userGetMatch && method === 'get') {
      const u = runtimeUsers.find(x => String(x.id) === userGetMatch[1]);
      if (!u) {
        reject(fail(config, 404, 'ユーザーが見つかりません'));
        return;
      }
      resolve(ok(config, u as unknown as Json));
      return;
    }
    // 所属病院（未対応だと「不明の管理」表示・フィルタ空になる）
    if (url === '/hospitals' && method === 'get') {
      resolve(ok(config, [DEMO_HOSPITAL] as unknown as Json));
      return;
    }

    // ---- 管理/破壊系操作は明示的に拒否（フォールバックで偽成功にしない）----
    // ユーザー管理（削除・権限変更・復元）、アカウント削除、動画の削除/トリミング/画像操作。
    // 特に DELETE /videos はフォールバックだと「削除しました」→ reload → 復活 という
    // 最悪の見え方になるため必ず拒否する。
    if (
      (url.startsWith('/users/') && method !== 'get') ||
      (url === '/auth/account' && method === 'delete') ||
      (url === '/videos' && method === 'delete') ||
      (url === '/videos/trim' && method === 'post') ||
      (/^\/videos\/[^/]+\/images$/.test(url) && method !== 'get') ||
      (url.startsWith('/video-images') && method !== 'get') ||
      (/^\/videos\/[^/]+\/materials$/.test(url) && method !== 'get') ||
      (url.startsWith('/video-materials') && method !== 'get')
    ) {
      reject(fail(config, 403, 'デモではこの操作はできません。'));
      return;
    }

    // ---- 接続元判定（デモは院内扱い）----
    // 未ハンドルだとフォールバックの [] から origin が取れず、VideoCard ごとに
    // 警告が出続ける。デモにバックエンドの院外制限は無いので internal を返す。
    if (url === '/access/origin' && method === 'get') {
      resolve(ok(config, { origin: 'internal' } as unknown as Json));
      return;
    }

    // ---- ログ送信は黙って成功扱い ----
    if (url === '/logs') {
      resolve(ok(config, { success: true }));
      return;
    }

    // ---- フォールバック（未知ルート）----
    // 未ハンドルのルートを黙って成功させると追加漏れに気づけないため、開発者向けに警告する。
    // GET はUIを壊さない空データ、それ以外は no-op success を返す。
    console.warn(`[demoAdapter] 未ハンドルのルート: ${method.toUpperCase()} ${url}`);
    if (method === 'get') {
      resolve(ok(config, [] as unknown as Json));
    } else {
      resolve(ok(config, { success: true } as unknown as Json));
    }
  });
};
